<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import {
    playerResults,
    getSessionData,
    verifyResult,
    fetchRunningTournament,
  } from "$lib/services/tournaments/endpoints";
  import TournamentCompletionOverlay from "./TournamentCompletionOverlay.svelte";

  export let gameUrl: string;
  export let tournamentId: number;
  export let username: string;
  export let spinsLimit: number = 10;

  let iframeHeight = 600;
  let originalHeight = 600;
  let playerSpins = 0;
  let toBeKilled = false;
  let isGameCompleted = false;
  let isVerifying = false;
  let apiURL: string | null = null;
  let token: string | null = null;
  let finalScore: number | null = null;
  let gameContainer: HTMLDivElement;
  let isFullscreen = false;
  let isNavigatingAway = false;
  let playerPosition: number | null = null;
  let tournamentData: any = null;

  const updateIframeHeight = () => {
    if (isFullscreen) return;

    if (window.innerWidth < 600) {
      iframeHeight = window.innerHeight * 0.3;
    } else {
      iframeHeight = originalHeight;
    }
    originalHeight = iframeHeight;
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        originalHeight = iframeHeight;
        await gameContainer.requestFullscreen();
        isFullscreen = true;
      } else {
        await document.exitFullscreen();
        isFullscreen = false;
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  };

  const handleFullscreenChange = () => {
    isFullscreen = !!document.fullscreenElement;

    if (isFullscreen) {
      iframeHeight = window.innerHeight;
    } else {
      iframeHeight = originalHeight;
    }
  };

  const handleResize = () => {
    if (!isFullscreen) {
      updateIframeHeight();
    } else {
      iframeHeight = window.innerHeight;
    }
  };

  const checkSpinsAndCompletion = async (spinsCount: number) => {
    if (spinsCount >= spinsLimit && !isVerifying && !isGameCompleted) {
      isGameCompleted = true;
      try {
        // Fetch latest tournament data before showing completion overlay
        const response = await fetchRunningTournament(tournamentId, username);
        tournamentData = response.data;
        const verificationResult = await verifyResult(tournamentId, username);
        if (verificationResult.success) {
          finalScore = verificationResult.data.score;
          playerPosition = verificationResult.data.position;
        }
      } catch (error) {
        console.error("Error verifying final result:", error);
      }
    }
  };

  const initializeSession = async () => {
    try {
      const sessionData = await getSessionData(tournamentId, username);

      if (sessionData?.data) {
        apiURL = sessionData.data.apiURL;
        token = sessionData.data.token;
        playerSpins = sessionData.data.playerSpins || 0;

        // If player has no spins left, set completion state immediately
        if (playerSpins >= spinsLimit) {
          isGameCompleted = true;
          try {
            // Get tournament data directly
            tournamentData = await fetchRunningTournament(tournamentId, username);

            // Get player results if API URL and token are available
            if (apiURL && token) {
              const results = await playerResults(apiURL, token);
              if (results) {
                finalScore = results.score;
                playerPosition = results.position;
              }
            }
          } catch (error) {
            console.error("Error fetching completion data:", error);
          }
        }
      } else {
        console.warn("Session data is empty or invalid:", sessionData);
      }
    } catch (error) {
      console.error("Failed to initialize session:", error);
    }
  };

  const updatePlayerStats = async () => {
    if (!apiURL || !token) {
      await initializeSession();
      return;
    }

    try {
      const results = await playerResults(apiURL!, token!);

      if (results && typeof results.spins_taken !== "undefined") {
        playerSpins = results.spins_taken;
        playerPosition = results.position;
        finalScore = results.score;

        const event = new CustomEvent("playerStatsUpdate", {
          detail: {
            scoreboard: results.scoreboard,
            score: results.score,
            spins_taken: results.spins_taken,
            spinsLeft: Math.max(0, spinsLimit - results.spins_taken),
            position: results.position,
          },
        });
        window.dispatchEvent(event);

        await checkSpinsAndCompletion(playerSpins);
      }
    } catch (error) {
      console.error("Error updating userStatistics:", error);
      apiURL = null;
      token = null;
    }
  };

  const handleBeforeUnload = () => {
    isNavigatingAway = true;
  };

  const receiveMessage = async (event: MessageEvent) => {
    if (isGameCompleted) return;

    const message = event.data;

    switch (message.name) {
      case "gameRoundStarted":
        playerSpins++;
        break;

      case "gameRoundEnded":
        await updatePlayerStats();
        break;

      case "notifyCloseContainer":
        if (!isGameCompleted && !isVerifying && playerSpins >= spinsLimit) {
          await checkSpinsAndCompletion(playerSpins);
        } else if (!isNavigatingAway) {
          await updatePlayerStats();
        }
        break;

      case "balanceTooLow":
        await updatePlayerStats();
        if (playerSpins >= spinsLimit && !isVerifying && !isGameCompleted) {
          toBeKilled = true;
          await checkSpinsAndCompletion(playerSpins);
        }
        break;
    }
  };

  onMount(async () => {
    await initializeSession();
    window.addEventListener("resize", handleResize);
    window.addEventListener("message", receiveMessage, false);
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    updateIframeHeight();
  });

  onDestroy(() => {
    window.removeEventListener("resize", handleResize);
    window.removeEventListener("message", receiveMessage);
    window.removeEventListener("beforeunload", handleBeforeUnload);
    document.removeEventListener("fullscreenchange", handleFullscreenChange);
  });
</script>

<div class="game-container" bind:this={gameContainer}>
  {#if gameUrl}
    <div class="game-controls">
      <button
        class="fullscreen-button"
        on:click={toggleFullscreen}
        title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
        {#if isFullscreen}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
          </svg>
        {:else}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        {/if}
      </button>
    </div>
    <iframe title="Tournament Game" src={gameUrl} width="100%" height={iframeHeight} frameborder="0" />
    {#if isGameCompleted && tournamentData}
      <TournamentCompletionOverlay {tournamentData} {playerPosition} {finalScore} />
    {/if}
  {:else}
    <p>No game available</p>
  {/if}
</div>

<style>
  .game-container {
    position: relative;
  }
  /* Fullscreen styles */
  .game-container:fullscreen {
    padding: 0;
    width: 100vw;
    height: 100vh;
  }

  .game-container:fullscreen iframe {
    height: 100vh;
  }

  .game-controls {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    opacity: 0.6;
    transition: opacity 0.3s ease;
  }

  .game-controls:hover {
    opacity: 1;
  }

  .fullscreen-button {
    background: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 4px;
    color: white;
    padding: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }

  .fullscreen-button:hover {
    background: rgba(0, 0, 0, 0.8);
  }
</style>
